// useFixMobileKeyboard.ts
import { useEffect } from 'react';

/**
 * Fixes mobile keyboard issues in WebView:
 * - Prevents page jumping / overlap with status bar.
 * - Ensures input fields scroll smoothly into view.
 * - Handles 100vh resizing bugs.
 */
export const useFixMobileKeyboard = () => {
  useEffect(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) return;

    // --- Handle scroll & viewport jump ---
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
    };

    const handleFocusIn = () => document.body.classList.add('keyboard-open');
    const handleFocusOut = () => document.body.classList.remove('keyboard-open');

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);

    document.addEventListener('focus', handleFocus, true);

    // --- 2️⃣ Adjust 100vh issue dynamically ---
    const updateVH = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
    };
    updateVH();
    window.addEventListener('resize', updateVH);

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('focus', handleFocus, true);
      window.removeEventListener('resize', updateVH);
    };
  }, []);
};